import React, { useState, useEffect, useMemo } from 'react'
import { Checkbox, ContextButton, SelectBox, TextInput } from '@tecuity/granite-ui'
import { CheckboxList } from './SettingsComponents/CheckboxList'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import Revert from '@tecuity/sos-icons/react/revert'
import './PropertiesList.scss'
import format from 'date-fns/format'
import * as api from 'requests'
import { Spinner } from 'components/Loaders/Loaders'
import { setErrorToast, setSuccessToast } from '../../../actions'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import ChangePassword from './ChangePassword'
import ResetPassword from './ResetPassword'
import ReceiptItemDistribution from '../Fiscal/ReceiptItemDistribution'
import { getStateOptions } from 'components/Wizard2/selectOptions'
import SettingsUserSearch from '../AccessControl/SettingsUserSearch'
import SettingsRegisteredAgentSearch from '../AccessControl/SettingsRegisteredAgentSearch'
import ImageClassificationDefinitions from '../Images/ImageClassificationDefinitions'
import DateRow from 'components/DateRow/DateRow'
import CommonList from './CommonList'
import UserRoleIdTable from '../WorkviewTools/UserRoleIdTable'
import LinkedUserAccounts from '../../Accounting/FrequentPayorAccounts/LinkedUserAccounts'
import SignatureByteArray from '../General/SignatureByteArray'
import RecordUserAccessConfiguredSourceTypes from '../General/RecordUserAccessConfiguredSourceTypes'
import RecordUserAccessConfiguredMiscellaneousModules from '../General/RecordUserAccessConfiguredMiscellaneousModules'
import { SUBROUTES } from '../../../App'

const PropertiesList = ({ itemId, allowAdd, isAdding, redirect, handleSaveSuccess, readOnly = false }) => {
  const dispatch = useDispatch()
  const { optionalData, defaultDTOs, pageProperties } = useSelector(
    (state) => state.settings,
    shallowEqual
  )
  const [editItem, setEditItem] = useState({})
  const [isDirty, setIsDirty] = useState(false)
  const [optionsMap, setOptionsMap] = useState({})
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [showChangeStaffPassword, setShowChangeStaffPassword] = useState(false) // needed here because of the button...
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false)

  useEffect(() => {
    pageProperties && setLoading(false)
  }, [pageProperties])

  const handleLoadSuccess = (res) => {
    setError('')
    setEditItem(res)
    setIsDirty(false)
    setLoading(false)
  }

  const handleError = (action, err) => {
    console.error(`Error ${action} ${redirect} settings list`, err)
    if (action === 'retrieving') setError(`Error ${action} ${redirect} settings list`)
    else dispatch(setErrorToast(`Error ${action} ${redirect} settings list`))
    setLoading(false)
  }

  useEffect(() => {
    if (isAdding) setEditItem(defaultDTOs[redirect] ? { ...defaultDTOs[redirect] } : {})
  }, [isAdding, redirect])

  useEffect(() => {
    if (!itemId) {
      setEditItem({ ...defaultDTOs[redirect] })
    } else {
      setLoading(true)
      //API call to fetch item
      switch (redirect) {
        case 'applicationSettings':
          api.GET_APP_SETTING_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'states':
          api.GET_SETTINGS_STATE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'countries':
          api.GET_SETTINGS_COUNTRY_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'counties':
          api.GET_SETTINGS_COUNTY_BY_ID(
            itemId,
            (res) =>
              handleLoadSuccess({
                ...res,
                StateCode: res.State ? res.State.StateCode || '' : '',
              }),
            (err) => handleError('retrieving', err)
          )
          break
        case 'postalCodes':
          api.GET_SETTINGS_POSTAL_CODE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'notaryBondCompanies':
          api.GET_SETTINGS_NOTARY_BOND_COMPANY_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'notaryFilingTypes':
          api.GET_SETTINGS_NOTARY_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'notaryRecordTypes':
          api.GET_SETTINGS_NOTARY_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'uccRecordTypes':
          api.GET_SETTINGS_UCC_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'uccFilingTypes':
          api.GET_SETTINGS_UCC_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'uccTypes':
          api.GET_SETTINGS_UCC_TYPE_BY_ID(
            // add me
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'uccStatusTypes':
          api.GET_SETTINGS_UCC_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'userXmlApi':
          api.GET_UCC_XML_USER_ACCESS_BY_ID(
            itemId,
            (res) => {
              const readOnlyData = {
                NameReadonly: res.User.FullName,
                EmailReadonly: res.User.Email,
                LastModifiedByName: res.GrantedBy.FullName,
                LastModifiedDate: res.GrantedDate,
              }
              handleLoadSuccess(readOnlyData)
            },
            (err) => handleError('retrieving', err)
          )
          break
        case 'businessXmlUserApi':
          api.GET_BUSINESS_XML_USER_ACCESS_BY_ID(
            itemId,
            (res) => {
              const readOnlyData = {
                UserId: res.UserId,
                NameReadonly: res.User.FullName,
                EmailReadonly: res.User.Email,
                RegisteredAgentNameReadonly: res.RegisteredAgentRecord
                  ? res.RegisteredAgentRecord.AgentName
                  : '',
                RegisteredAgentRecordId: res.RegisteredAgentRecordId,
                LastModifiedByName: res.GrantedBy ? res.GrantedBy.FullName : '',
                LastModifiedDate: res.GrantedDate,
              }
              handleLoadSuccess(readOnlyData)
            },
            (err) => handleError('retrieving', err)
          )
          break
        case 'notaryStatusTypes':
          api.GET_SETTINGS_NOTARY_STATUS_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'complaintReasonTypes':
          api.GET_SETTINGS_NOTARY_COMPLAINT_REASON_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break

        case 'complaintStatusTypes':
          api.GET_SETTINGS_NOTARY_COMPLAINT_STATUS_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'customCorrespondence':
          api.GET_SETTINGS_CUSTOM_CORRESPONDENCE_REASON_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'userDetails':
          api.GET_SETTINGS_APP_USERS_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          api.GET_SETTINGS_COUNTIES(
            (res) => {
              setOptionsMap({
                ...optionsMap,
                countyOptions: res.map((item) => {
                  return {
                    label: item.Name,
                    value: item.Id.toString(),
                  }
                }),
              })
            },
            (err) => {
              console.error(err)
            }
          )
          break
        case 'userRolesAndRights':
          api.GET_SETTINGS_ROLE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'notarySpecialServiceProviders':
          api.GET_SOFTWARE_PROVIDER(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'receiptItems':
          api.GET_SETTINGS_RECEIPT_ITEM_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'generalLedgerAccounts':
          api.GET_SETTINGS_GENERAL_LEDGER_ACCOUNT_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'collateralKeywords':
          api.GET_SETTINGS_REGULATED_WORD_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'regulatedWords':
          api.GET_SETTINGS_REGULATED_WORD_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'workViews':
          api.GET_SETTINGS_WORKVIEW_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'wordManagement':
          api.GET_SETTINGS_REPLACEMENT_WORD_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'businessFilingTypes':
          api.GET_SETTINGS_BUSINESS_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'businessRecordTypes':
          api.GET_SETTINGS_BUSINESS_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'businessRecordSubTypes':
          api.GET_SETTINGS_BUSINESS_RECORD_SUB_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'businessStatusTypes':
          api.GET_SETTINGS_BUSINESS_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'businessPartyTitle':
          api.GET_SETTINGS_BUSINESS_PARTY_TITLE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'businessPartyType':
          api.GET_SETTINGS_BUSINESS_PARTY_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'imagePageType':
          api.GET_SETTINGS_IMAGE_PAGE_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'processedThroughDates':
          api.GET_PROCESSED_THROUGH_DATE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'orderFilingTypes':
          api.GET_SETTINGS_ORDER_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'trademarkApplicantTypes':
          api.GET_SETTINGS_TRADEMARK_APPLICANT_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'trademarkFilingTypes':
          api.GET_SETTINGS_TRADEMARK_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'trademarkRecordTypes':
          api.GET_SETTINGS_TRADEMARK_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'trademarkStatusTypes':
          api.GET_SETTINGS_TRADEMARK_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'trademarkClassTypes':
          api.GET_SETTINGS_TRADEMARK_CLASS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'signerCapacityTypes':
          api.GET_SIGNER_CAPACITY_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess({ ...res, SourceTypeId: res.SourceTypeId.toString() }),
            (err) => handleError('retrieving', err)
          )
          break
        case 'trademarkApplicantOrganizationTypes':
          api.GET_SETTINGS_TRADEMARK_APPLICANT_ORG_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'registeredAgentRecordTypes':
          api.GET_RA_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'registeredAgentFilingTypes':
          api.GET_RA_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'registeredAgentStatusTypes':
          api.GET_RA_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'athleteAgentRecordTypes':
          api.GET_ATHLETE_AGENT_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'athleteAgentFilingTypes':
          api.GET_ATHLETE_AGENT_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'athleteAgentStatusTypes':
          api.GET_ATHLETE_AGENT_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'serviceOfProcessStatutes':
          api.GET_SERVICE_OF_PROCESS_STATUTE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'serviceOfProcessCourts':
          api.GET_SERVICE_OF_PROCESS_COURT_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'formPermissionSettings':
          api.GET_FORM_PERMISSIONS_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'historicSOS':
          api.GET_HISTORIC_SOS_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'recordUserAccess':
          api.GET_RECORD_USER_ACCESS_ROLE_BY_ID(
            itemId,
            (res) => {
              const AppliesToIds = [],
                PasskeyDefaultIds = [],
                RequestAccessDefaultIds = [],
                LoggedInDefaultIds = [],
                NotLoggedInDefaultIds = []
              Object.keys(res.ConfiguredSourceTypes).forEach((key) => {
                const value = res.ConfiguredSourceTypes[key]
                AppliesToIds.push(Number(key))
                if (value.UseAsPasskeyDefault) PasskeyDefaultIds.push(Number(key))
                if (value.UseAsRequestAccessDefault) RequestAccessDefaultIds.push(Number(key))
                if (value.UseAsLoggedInDefault) LoggedInDefaultIds.push(Number(key))
                if (value.UseAsNotLoggedInDefault) NotLoggedInDefaultIds.push(Number(key))
              })
              const MiscAppliesToIds = [],
                MiscPasskeyDefaultIds = [],
                MiscRequestAccessDefaultIds = [],
                MiscLoggedInDefaultIds = [],
                MiscNotLoggedInDefaultIds = []
              Object.keys((res.ConfiguredMiscellaneousModules) || {}).forEach((key) => {
                const value = res.ConfiguredMiscellaneousModules[key]
                MiscAppliesToIds.push(Number(key))
                if (value.UseAsPasskeyDefault) MiscPasskeyDefaultIds.push(Number(key))
                if (value.UseAsRequestAccessDefault) MiscRequestAccessDefaultIds.push(Number(key))
                if (value.UseAsLoggedInDefault) MiscLoggedInDefaultIds.push(Number(key))
                if (value.UseAsNotLoggedInDefault) MiscNotLoggedInDefaultIds.push(Number(key))
              })
              const parsedRes = {
                ...res,
                AppliesToIds,
                PasskeyDefaultIds,
                RequestAccessDefaultIds,
                LoggedInDefaultIds,
                NotLoggedInDefaultIds,
                MiscAppliesToIds,
                MiscPasskeyDefaultIds,
                MiscRequestAccessDefaultIds,
                MiscLoggedInDefaultIds,
                MiscNotLoggedInDefaultIds,
              }
              handleLoadSuccess(parsedRes)
            },
            (err) => handleError('retrieving', err)
          )
          break
        case 'charityFilingTypes':
          api.GET_CHARITY_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'charityStatusTypes':
          api.GET_CHARITY_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'charityRecordTypes':
          api.GET_CHARITY_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break
        case 'contractorFilingTypes':
          api.GET_CONTRACTOR_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'contractorStatusTypes':
          api.GET_CONTRACTOR_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'contractorRecordTypes':
          api.GET_CONTRACTOR_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'contractorClassTypes':
          api.GET_CONTRACTOR_CLASS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'contractorComplaintTypes':
          api.GET_CONTRACTOR_COMPLAINT_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'contractorComplaintStatusTypes':
          api.GET_CONTRACTOR_COMPLAINT_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'contractorComplaintReasonTypes':
          api.GET_CONTRACTOR_COMPLAINT_REASON_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'lobbyistFilingTypes':
          api.GET_LOBBYIST_FILING_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'lobbyistStatusTypes':
          api.GET_LOBBYIST_STATUS_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'lobbyistRecordTypes':
          api.GET_LOBBYIST_RECORD_TYPE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        case 'miscellaneousModules':
          api.GET_MISCELLANEOUS_MODULE_BY_ID(
            itemId,
            (res) => handleLoadSuccess(res),
            (err) => handleError('retrieving', err)
          )
          break;
        default:
          setLoading(false)
          setError(`Error ${redirect} Property Endpoints NOT tied in.`)
      }
      setIsDirty(false)
    }
  }, [itemId])

  const getCheckboxTooltip = (key) => {
    switch (key) {
      case 'NameIsDistinguishable':
        return 'something to be updated'
      case 'ShowInPublicHistory':
        return 'If checked, filing will appear in the View History pop-out of the individual record.'
      case 'IsActive':
        switch (redirect) {
          case 'notaryStatusTypes':
            return 'The "Is Active" flag controls if the Status Type itself is active. Statuses without this checked will not appear in the "Record to Filing Type" or Notary Public Search settings.'
          default:
            return ''
        }
      case 'IsActiveStatus':
        switch (redirect) {
          case 'notaryStatusTypes':
            return 'The "Is Active Status" flag controls what information is displayed when searching for a Notary when processing an Apostille. Records with this indicator marked will appear as active in the results display.'
          default:
            return ''
        }
      case 'RedFlagStatus':
        switch (redirect) {
          case 'notaryStatusTypes':
            return 'If checked, then the status will appear red in the Public Search drawer, My Records drawer, and Staff Search results and drawer.'
          default:
            return ''
        }
      case 'MatchStringIsRegularExpression':
        return 'This value is set via the code.  Please contact Tecuity to adjust if needed.'
      default:
        return ''
    }
  }

  const getInputTooltip = (key) => {
    switch (key) {
      case 'SortOrder':
        switch (redirect) {
          case 'customCorrespondence':
            return 'If used, this sort order will override the alphabetical default in place for how correction/rejection reasons are displayed on the custom correspondence.'
          default:
            return ''
        }
      default:
        return ''
    }
  }

  const listPropertyComponent = (property) => {
    const label = property.k
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
      .trim()

    switch (property.k) {
      case 'AnnualReportMonthDue':
      case 'ExpenditureReportMonthDue':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap.FilingMonthDueOptions || []}
            value={editItem[property.k] ? editItem[property.k] : ''}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
            disabled={
              (!editItem.RequiresAnnualReport && !editItem.RequiresExpenditureReport) ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false
            }
          />
        )
      case 'AnnualReportDayDue':
      case 'ExpenditureReportDayDue':
        const options = []
        for (let i = 1; i < 32; i++) {
          options.push({ label: i.toString(), value: i })
        }
        options.push({ label: 'Last Day of the Month', value: -1 })
        return (
          <SelectBox
            key={property.k}
            options={options}
            value={editItem[property.k] ? editItem[property.k] : ''}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
            disabled={
              (!editItem.RequiresAnnualReport && !editItem.RequiresExpenditureReport) ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false
            }
          />
        )
      case 'AnnualReportFrequencyTypeId':      
        return (
          <SelectBox
            key={property.k}
            options={optionsMap.filingFrequencyTypeOptions || []}
            value={editItem[property.k] ? editItem[property.k] : ''}
            label={`Annual Report Frequency Type`}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
            disabled={
              !editItem.RequiresAnnualReport ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false
            }
            noPlaceholder
          />
        )
      case 'PublicSearchPlaceholderText':
        return (
          <TextInput
            key={property.k}
            label={label}
            value={property.v || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
            disabled={
              !editItem.HasPublicSearch ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              readOnly
            }
          />
        )
      case 'PublicSearchDescriptionMarkdownText':
        return (
            <div style={{fontSize: '0.9em'}} key={property.k}>
              <div className="property-label">{label}</div>
              <textarea
                rows="4"
                value={property.v}
                onChange={(e) => {
                  setEditItem({ ...editItem, [property.k]: e.target.value })
                  setIsDirty(true)
                }}
                disabled={
                  !editItem.HasPublicSearch ||
                  (pageProperties.find((prop) => prop.Name == property.k) &&
                    pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
                  readOnly}
              ></textarea>
            </div>
        )
      case 'State':
        if (redirect === 'counties') {
          return (
            <SelectBox
              key={'StateCode'}
              options={getStateOptions(true)}
              value={editItem['StateCode'] ? editItem['StateCode'] : ''}
              label={label}
              onChange={(e) => {
                setEditItem({
                  ...editItem,
                  StateCode: e,
                })
                setIsDirty(true)
              }}
            />
          )
        } else {
          return (
            <SelectBox
              key={property.k}
              options={getStateOptions(true)}
              value={editItem[property.k] ? editItem[property.k] : ''}
              label={label}
              onChange={(e) => {
                setEditItem({
                  ...editItem,
                  [property.k]: e,
                })
                setIsDirty(true)
              }}
            />
          )
        }
      case 'CourtType':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['SOPCourtTypeOptions']}
            value={editItem.CourtType ? editItem.CourtType.toString() : ''}
            label={label}
            noPlaceholder
            onChange={(e) => {
              setEditItem({
                ...editItem,
                CourtType: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'RecordTypeId':
        // console.log(editItem)
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['recordTypeOptions']}
            value={
              editItem[property.k] || +editItem[property.k] === 0
                ? editItem[property.k].toString()
                : ''
            }
            label={'Record Type'}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: +e,
                RecordSubTypeId: editItem.RecordSubTypeId !== undefined ? 0 : undefined,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'TrademarkRecordType':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['trademarkRecordTypeOptions']}
            value={editItem.TrademarkRecordTypeId ? editItem.TrademarkRecordTypeId.toString() : ''}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                TrademarkRecordTypeId: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'RecordSubTypeId':
        const subTypeOptions = optionsMap['recordSubTypeOptions'] || []
        const recordTypeSubTypeXref = optionsMap['recordTypeSubTypeXref'] || []
        return (
          <SelectBox
            key={property.k}
            options={subTypeOptions.filter(
              (item) => +item.value === 0 || recordTypeSubTypeXref.includes(+item.value)
            )}
            value={
              editItem[property.k] || +editItem[property.k] === 0
                ? editItem[property.k].toString()
                : ''
            }
            label={'Record Sub Type'}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: +e,
              })
              setIsDirty(true)
            }}
            disabled={
              recordTypeSubTypeXref.length === 0 ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false
            }
          />
        )
      // case 'ReadOnlyRoles':
      //   if (!optionsMap['userRoleOptions'])
      //     return (
      //       <div className="inline-spinner" key="inline-spinner">
      //         <Spinner size="small" themeColor="dark-neutral" />
      //       </div>
      //     )
      //   const checkedList = []
      //   ;(optionsMap['userRoleOptions'] || []).forEach((role) => {
      //     if (itemId && role.WorkviewIds.includes(+itemId)) checkedList.push(role.RoleId.toString())
      //   })
      //   return (
      //     <CheckboxList
      //       key={property.k}
      //       items={(optionsMap['userRoleOptions'] || []).map((item) => ({
      //         label: `${item.Name} - ${item.Category}`,
      //         id: item.RoleId.toString(),
      //       }))}
      //       label={'Roles - Configure in Access Control setting '}
      //       checkedList={checkedList}
      //       disabled
      //     />
      //   )
      case 'UsersWithRole':
        return (
          <div key={property.k}>
            <div className="property-label">Users With Role (edit from User Details setting)</div>
            <div className="settings-list-wrapper">
              <CommonList
                list={(editItem[property.k] || []).sort().map((username, index) => ({
                  Name: username,
                  Id: index,
                }))}
                itemTitle="users"
              />
            </div>
          </div>
        )
      case 'RightIds':
        if (!optionsMap['rightOptions'])
          return (
            <div className="inline-spinner" key={property.k}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )
        return (
          <CheckboxList
            key={property.k}
            items={optionsMap['rightOptions'] || []}
            label={'Rights'}
            checkedList={(editItem[property.k] || []).map((id) => id.toString())}
            setActiveChanges={(checked) => {
              setEditItem({
                ...editItem,
                [property.k]: checked.map((id) => Number(id)),
              })
              setIsDirty(true)
            }}
          />
        )
      case 'UserRoleIds':
        if (!optionsMap['userRoleOptions'])
          return (
            <div className="inline-spinner" key={property.k}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )
        if (redirect === 'workViews') {
          return (
            <UserRoleIdTable
              key={property.k}
              property={property}
              userRoleIds={editItem.UserRoleIds}
              userRoleProcessIds={editItem.UserRoleProcessIds}
              optionsMap={optionsMap}
              onChange={(val) => {
                setIsDirty(true)
                setEditItem({
                  ...editItem,
                  UserRoleIds: val.UserRoleIds,
                  UserRoleProcessIds: val.UserRoleProcessIds,
                })
              }}
            />
          )
        }

        return (
          <CheckboxList
            key={property.k}
            items={(optionsMap['userRoleOptions'] || []).map((item) => ({
              label: `${item.Name} - ${item.Category}`,
              id: item.RoleId.toString(),
            }))}
            label={'Roles'}
            checkedList={(editItem[property.k] || []).map((id) => id.toString())}
            setActiveChanges={(checked) => {
              setEditItem({
                ...editItem,
                [property.k]: checked.map((id) => Number(id)),
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ConfiguredSourceTypes':
        return (
          <div key={property.k}>
            {(optionsMap['miscModuleOptions'] || []).length > 0 && (
              <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>Source Types</p>
            )}
            <RecordUserAccessConfiguredSourceTypes
              key={property.k}
              property={property}
              editItem={editItem}
              optionsMap={optionsMap}
              onChange={(val) => {
                setIsDirty(true)
                setEditItem({
                  ...editItem,
                  ...val,
                })
              }}
            />
            {(optionsMap['miscModuleOptions'] || []).length > 0 && (
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>Miscellaneous Modules</p>
                <RecordUserAccessConfiguredMiscellaneousModules
                  key={property.k + '-misc'}
                  property={property}
                  editItem={editItem}
                  onChange={(val) => {
                    setIsDirty(true)
                    setEditItem({
                      ...editItem,
                      ...val,
                    })
                  }}
                  optionsMap={optionsMap}
                />
              </div>
            )}
          </div>
        )
      case 'Roles':
        if (!optionsMap['userRoleOptions'])
          return (
            <div className="inline-spinner" key={property.k}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )
        return (
          <CheckboxList
            key={property.k}
            items={(optionsMap['userRoleOptions'] || []).map((item) => ({
              label: `${item.Name} - ${item.Category}`,
              id: item.RoleId.toString(),
            }))}
            label={label}
            checkedList={(editItem[property.k] || []).map((role) => role.RoleId.toString())}
            setActiveChanges={(checked) => {
              setEditItem({
                ...editItem,
                [property.k]: (optionsMap['userRoleOptions'] || []).filter((item) =>
                  checked.includes(item.RoleId.toString())
                ),
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ImagePageTypes':
        return (
          <CheckboxList
            key={property.k}
            items={(optionsMap['imagePageTypeOptions'] || []).map((item) => ({
              label: `${item.Name}`,
              id: item.Id.toString(),
            }))}
            label={label}
            checkedList={(editItem[property.k] || []).map((item) => item.toString())}
            setActiveChanges={(checked) => {
              setEditItem({
                ...editItem,
                [property.k]: checked.map((id) => Number(id)),
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ProcessingStatusTypeIds':
        // console.log(editItem)
        return (
          <CheckboxList
            key={property.k}
            items={(optionsMap['processingStatuses'] || []).map((item) => ({
              label: `${item.Name}`,
              id: item.Id.toString(),
            }))}
            label={`Processing Statuses`}
            checkedList={(editItem[property.k] || []).map((item) => item.toString())}
            setActiveChanges={(checked) => {
              setEditItem({
                ...editItem,
                [property.k]: checked.map((id) => Number(id)),
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ServiceTypes':
        return (
          <CheckboxList
            key={property.k}
            items={optionsMap['softwareTypeOptions'] || []}
            label={label}
            checkedList={editItem[property.k] || []}
            setActiveChanges={(checked) => {
              setEditItem({ ...editItem, [property.k]: checked })
              setIsDirty(true)
            }}
          />
        )
      case 'BusinessPartyTitleIds':
        // console.log(optionsMap)
        return (
          <CheckboxList
            key={property.k}
            items={optionsMap['businessPartyTitleOptions'] || []}
            label={'Business Party Titles'}
            checkedList={editItem[property.k] || []}
            setActiveChanges={(checked) => {
              setEditItem({ ...editItem, [property.k]: checked })
              setIsDirty(true)
            }}
          />
        )
      case 'NotaryComplaintTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['notaryComplaintReasonsTypeOptions'] || []}
            value={editItem.NotaryComplaintTypeId ? editItem.NotaryComplaintTypeId.toString() : ''}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )      
      case 'UCCLapseTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['uccLapseTypeOptions'] || []}
            value={editItem.UCCLapseTypeId ? editItem.UCCLapseTypeId.toString() : ''}
            label={'UCC Lapse Type'}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ImagePageTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['imagePageTypeOptions'] || []}
            value={editItem.ImagePageTypeId ? editItem.ImagePageTypeId.toString() : ''}
            label={'Image Page Type'}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'UserType':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['userTypeOptions'] || []}
            value={editItem.UserTypeId ? editItem.UserTypeId.toString() : ''}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: { Id: e },
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ReceiptItemCategoryTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['receiptItemCategoryTypeOptions'] || []}
            value={
              editItem.ReceiptItemCategoryTypeId
                ? editItem.ReceiptItemCategoryTypeId.toString()
                : ''
            }
            label={`Category`}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'BatchTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['batchTypeOptions'] || []}
            value={editItem.BatchTypeId ? editItem.BatchTypeId.toString() : ''}
            label={`Batch Type`}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ImagePageTypeCategory':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['imagePageTypeCategories'] || []}
            value={editItem.ImagePageCategoryId ? editItem.ImagePageCategoryId : 0}
            label={`Category`}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                ImagePageCategoryId: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'AdditionalPageImagePageTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['additionalPageTypes'] || []}
            value={
              editItem.AdditionalPageImagePageTypeId
                ? editItem.AdditionalPageImagePageTypeId.toString()
                : ''
            }
            label={`Additional Page Type`}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'DefaultAzureStorageAccountConfigId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['azureStorageAccounts'] || []}
            value={
              editItem.DefaultAzureStorageAccountConfigId
                ? editItem.DefaultAzureStorageAccountConfigId.toString()
                : ''
            }
            label={`Azure Storage Account`}
            disabled={
              !editItem.IsFirstPage ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false
            }
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
            noPlaceholder
          />
        )
      case 'SourceTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['sourceTypeOptions'] || []}
            value={editItem.SourceTypeId ? editItem.SourceTypeId.toString() : ''}
            label={`Source Type`}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ProcessedThroughDateTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['processedThroughDateTypeOptions'] || []}
            value={
              editItem.ProcessedThroughDateTypeId
                ? editItem.ProcessedThroughDateTypeId.toString()
                : ''
            }
            label={`Processed Through Date Type`}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'UnitType':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['unitTypeOptions'] || []}
            value={editItem.UnitTypeId ? editItem.UnitTypeId.toString() : ''}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: { Id: e },
              })
              setIsDirty(true)
            }}
          />
        )
      case 'RegulatedWordTypeId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['collateralKeywordTypeOptions'] || []}
            value={editItem.RegulatedWordTypeId ? editItem.RegulatedWordTypeId.toString() : ''}
            label={
              redirect === 'collateralKeywords' ? 'Collateral Keyword Type' : 'Regulated Word Type'
            }
            onChange={(e) => {
              setEditItem({
                ...editItem,
                RegulatedWordTypeId: +e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'CountyAccess':
      case 'County':
        if (redirect === 'serviceOfProcessCourts')
          return (
            <SelectBox
              key={property.k}
              options={countyOptions || []}
              value={property.v || ''}
              label={label}
              onChange={(e) => {
                setEditItem({ ...editItem, [property.k]: e })
                setIsDirty(true)
              }}
            />
          )
        return (
          <SelectBox
            key={property.k}
            options={countyOptions || []}
            value={property.v ? property.v.Id : ''}
            label={label}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: { Id: e } })
              setIsDirty(true)
            }}
          />
        )
      case 'WordReplacementCategoryId':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['wordReplacementCategoryOptions'] || []}
            value={property.v}
            label={'Category'}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e })
              setIsDirty(true)
            }}
          />
        )
      case 'ContractorComplaintTypeId':
        if (redirect === 'contractorComplaintReasonTypes') {
          return (
          <SelectBox
            key={property.k}
            options={optionsMap['contractorComplaintReasonsTypeOptions'] || []}
            value={editItem.ContractorComplaintTypeId ? editItem.ContractorComplaintTypeId : -1}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                ContractorComplaintTypeId: e,
              })
              setIsDirty(true)
            }}
          />
        )
        } else {
          return !isAdding ? (
          <div key={property.k} className="property-readonly">
            <label>
              {`Id`}
              :&nbsp;
            </label>
            <div>{property.v || editItem.Id}</div>
          </div>
        ) : null
      }      
      case 'MiscellaneousSourceTypeConfiguration':
        return (
          <SelectBox
            key={property.k}
            options={optionsMap['miscellaneousSourceTypeConfigurationOptions'] || []}
            value={editItem.MiscellaneousSourceTypeConfiguration ? editItem.MiscellaneousSourceTypeConfiguration : -1}
            label={label}
            disabled={readOnly}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                MiscellaneousSourceTypeConfiguration: e,
              })
              setIsDirty(true)
            }}
          />
        )      
      case 'NotaryFilingTypeId':
      case 'NotaryRecordTypeId':
      case 'NotaryStatusTypeId':
      case 'UCCRecordTypeId':
      case 'UCCFilingTypeId':
      case 'UCCStatusTypeId':
      case 'DisplayId':
      case 'BusinessFilingTypeId':
      case 'BusinessRecordTypeId':
      case 'BusinessRecordSubTypeId':
      case 'BusinessStatusTypeId':
      case 'BusinessPartyTitleId':
      case 'CharityStatusTypeId':
      case 'OrderFilingTypeId':
      case 'RegisteredAgentRecordTypeId':
      case 'RegisteredAgentFilingTypeId':
      case 'RegisteredAgentStatusTypeId':
      case 'RecordAccessUserRoleId':
      case 'AthleteAgentRecordTypeId':
      case 'AthleteAgentFilingTypeId':
      case 'AthleteAgentStatusTypeId':
      case 'StatuteId':
      case 'TrademarkApplicantTypeId':
      case 'TrademarkApplicantOrgTypeId':
      case 'TrademarkClassTypeId':
      case 'TrademarkFilingTypeId':
      case 'TrademarkRecordTypeId':
      case 'TrademarkStatusTypeId':
      case 'CharityFilingTypeId':
      case 'CharityRecordTypeId':
      case 'ContractorFilingTypeId':
      case 'ContractorStatusTypeId':
      case 'ContractorRecordTypeId':
      case 'ContractorClassTypeId':      
      case 'ContractorComplaintStatusTypeId':
      case 'ContractorComplaintReasonTypeId':
      case 'LobbyistFilingTypeId':
      case 'LobbyistStatusTypeId':
      case 'LobbyistRecordTypeId':
      case 'MiscellaneousModuleId':
        return !isAdding ? (
          <div key={property.k} className="property-readonly">
            <label>
              {`Id`}
              :&nbsp;
            </label>
            <div>{property.v || editItem.Id}</div>
          </div>
        ) : null
      case 'CountyId':
      case 'Id':
      case 'UserRoleProcessIds':
      case 'AppliesToIds':
      case 'PasskeyDefaultIds':
      case 'RequestAccessDefaultIds':
      case 'LoggedInDefaultIds':
      case 'NotLoggedInDefaultIds':
      case 'MiscAppliesToIds':
      case 'MiscPasskeyDefaultIds':
      case 'MiscRequestAccessDefaultIds':
      case 'MiscLoggedInDefaultIds':
      case 'MiscNotLoggedInDefaultIds':
        return null
      case 'FullName':
      case 'ShortDescription':
      case 'Description':
        if (redirect !== 'complaintReasonTypes' && redirect !== 'applicationSettings' && redirect !== 'contractorComplaintReasonTypes') {
          return (
            <TextInput
              key={property.k}
              label={property.k === 'ShortDescription' ? 'Staff Description' : label}
              value={property.v || ''}
              onChange={(e) => {
                setEditItem({ ...editItem, [property.k]: e.target.value })
                setIsDirty(true)
              }}
            />
          )
        } else {
          return (
            <div key={property.k}>
              <div className="property-label">{label}</div>
              <textarea
                rows="4"
                value={property.v}
                onChange={(e) => {
                  setEditItem({ ...editItem, [property.k]: e.target.value })
                  setIsDirty(true)
                }}
                disabled={
                  (pageProperties.find((prop) => prop.Name == property.k) &&
                    pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
                  editItem.WriteableByWebAdmin === false
                }
              ></textarea>
            </div>
          )
        }
      case 'LongDescription':
      case 'Reason':
      case 'Information':
      case 'Value':
        return (
          <div key={property.k}>
            <div className="property-label">
              {property.k === 'LongDescription' ? 'Customer Description' : label}
            </div>
            <textarea
              rows="4"
              value={property.v}
              onChange={(e) => {
                setEditItem({ ...editItem, [property.k]: e.target.value })
                setIsDirty(true)
              }}
              disabled={
                (pageProperties.find((prop) => prop.Name == property.k) &&
                  pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
                editItem.WriteableByWebAdmin === false
              }
            ></textarea>
          </div>
        )
      case 'ValidationMessage':
        return (
          <div key={property.k}>
            <div className="property-label">
              {property.k === 'LongDescription' ? 'Customer Description' : label}
            </div>
            <textarea
              rows="4"
              value={property.v}
              disabled={
                Number(editItem.RegulatedWordTypeId) === 2 ||
                (pageProperties.find((prop) => prop.Name == property.k) &&
                  pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
                editItem.WriteableByWebAdmin === false
              }
              onChange={(e) => {
                setEditItem({ ...editItem, [property.k]: e.target.value })
                setIsDirty(true)
              }}
            ></textarea>
          </div>
        )
      case 'NameReadonly':
      case 'EmailReadonly':
        const thisLabel = property.k.includes('Readonly')
          ? property.k.replace('Readonly', '')
          : label
        return (
          <div key={property.k} className="property-readonly">
            <label>
              {thisLabel}
              :&nbsp;
            </label>
            <div>{property.v}</div>
          </div>
        )
      case 'LastModifiedByUserFullName':
      case 'LastModifiedByName':
      case 'WizardDatabaseField':
        return isAdding ? null : (
          <div key={property.k} className="property-readonly">
            <label>
              {label}
              :&nbsp;
            </label>
            <div>{property.v}</div>
          </div>
        )
      case 'StartDate':
      case 'EndDate':
        return (
          <div key={property.k}>
            <div className="property-label">{`${label}\n`}</div>
            <DateRow
              rowLabel={label}
              date={
                property.v && property.v.length >= 10
                  ? format(new Date(property.v), 'MM/dd/yyyy')
                  : property.v || ''
              }
              setDate={(e) => {
                setEditItem({ ...editItem, [property.k]: e })
                setIsDirty(true)
              }}
              maxDate={new Date()}
            />
          </div>
        )
      case 'Date':
        return (
          <div key={property.k}>
            <div className="property-label">{`${label}\n`}</div>
            <DateRow
              rowLabel="Processed Through Date"
              date={
                property.v && property.v.length >= 10
                  ? format(new Date(property.v), 'MM/dd/yyyy')
                  : property.v || ''
              }
              setDate={(e) => {
                setEditItem({ ...editItem, [property.k]: e })
                setIsDirty(true)
              }}
              showTodayButton
              maxDate={new Date()}
              disabled={
                +editItem.ProcessedThroughDateTypeId !== 1 ||
                (pageProperties.find((prop) => prop.Name == property.k) &&
                  pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
                editItem.WriteableByWebAdmin === false
              }
            />
          </div>
        )
      case 'PasswordExpiredDate':
        return (
          <div key={property.k} className="property-readonly">
            <label>
              {label}
              :&nbsp;
            </label>
            <div>
              {property.v ? format(new Date(property.v), 'M/dd/yyyy hh:mm aa') : `Does Not Expire`}
            </div>
          </div>
        )
      case 'LastPublicationDate':
      case 'LastModifiedDate':
        return isAdding || !property.v ? null : (
          <div key={property.k} className="property-readonly">
            <label>
              {label}
              :&nbsp;
            </label>
            <div>{format(new Date(property.v), 'M/dd/yyyy hh:mm aa')}</div>
          </div>
        )
      case 'ISO3166':
        return (
          <TextInput
            customCss={`width: 20%; min-width: 150px;`}
            key={property.k}
            label={label}
            value={property.v || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
      case 'ThreeDigitCode':
        return (
          <TextInput
            customCss={`width: 20%; min-width: 150px;`}
            key={property.k}
            label={label}
            value={property.v || ''}
            maxLength={3}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
        case 'Email':
        case 'Username':
        return (
          <TextInput
            key={property.k}
            label={label}
            value={property.v || ''}
            maxLength={100}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
      case 'LangCode':
        return (
          <TextInput
            customCss={`width: 20%; min-width: 150px;`}
            key={property.k}
            label="Language Code"
            value={property.v || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
      case 'ChangePassword':
        return isAdding ? null : (
          <div key={property.k} style={{ display: 'flex', gap: '8px' }}>
            <ContextButton
              onClick={() => {
                setShowChangeStaffPassword(true)
              }}
              variant="primary"
              leftIcon={
                <div className="checkmark-wrapper left">
                  <Revert />
                </div>
              }
            >
              Change Password
            </ContextButton>
            <ContextButton
              onClick={() => {
                setShowResetPasswordConfirm(true)
              }}
              variant="primary"
              customCss="margin-left: auto;"
            >
              Reset Password
            </ContextButton>
          </div>
        )
      case 'ClassNum':
        return (
          <TextInput
            customCss={`width: 20%; min-width: 150px;`}
            key={property.k}
            label={label}
            value={property.v || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
            type="number"
            min="0"
            step="1"
            pattern="\d+"
          />
        )
      case 'ForCorrection':
        return (
          <Checkbox
            key={property.k}
            checked={property.v || false}
            label="Available to Correction"
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e })
              setIsDirty(true)
            }}
          />
        )
      case 'ForRejection':
        return (
          <Checkbox
            key={property.k}
            checked={property.v || false}
            label="Available to Rejection"
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e })
              setIsDirty(true)
            }}
          />
        )
      case 'IsHagueConvention':
        return (
          <Checkbox
            key={property.k}
            checked={property.v || false}
            label="Participates in Hague Convention"
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e })
              setIsDirty(true)
            }}
          />
        )
      case 'AddToNote':
      case 'Active':
      case 'StartsWith':
      case 'BeginsWith':
      case 'CanFileAmendments':
      case 'CanFileOrderRequests':
      case 'CanFileSpecialAmendments':
      case 'CanManageUserAccess':
      case 'CanRequestAccess':
      case 'CanRequestCertificates':
      case 'CanViewInMyRecords':
      case 'CanViewHistory':
      case 'Contains':
      case 'EndsWith':
      case 'Encrypt':
      case 'FeeOverride':
      case 'IsActive':
      case 'AllowCorrection':
      case 'AllowRejection':
      case 'IsActiveOnline':
      case 'IsActiveStatus':
      case 'RedFlagStatus':
      case 'IsLocked':
      case 'IsPublic':
      case 'IsInitialFiling':
      case 'IsStaff':
      case 'IsInitialFilingType':
      case 'IsRenewal':
      case 'IsSystemAccount':
      case 'IsSystemAmendment':
      case 'IsConversion':
      case 'IsMerger':
      case 'IsAnnualReport':
      case 'IsForeignRecord':
      case 'IsNameReservation':
      case 'IsUsaClass':
      case 'IsLapsed':
      case 'IsTerminated':
      case 'MatchAtBeginning':
      case 'MatchContains':
      case 'MatchAtEnd':
      case 'MatchWholeWord':
      case 'IsNameDesignation':
      case 'RequiresAnnualReport':
      case 'RequiresAgent':
      case 'EntityCanConvert':
      case 'EntityCanMerge':
      case 'EntityCanBeRegisteredAgent':
      case 'RequiresNameAvailability':
      case 'RequiresForeignNameAvailability':
      case 'RequireApprovalDocumentation':
      case 'NameIsDistinguishable':
      case 'ShowInPublicHistory':
      case 'ShowInSearch':
      case 'ShowOnlineMessage':
      case 'WebPreparedOnly':
      case 'MatchStringIsRegularExpression':
      case 'ShowInPublicSearch':
      case 'UseForAutoBusinessFilings':
      case 'IsExpenditureReport':
      case 'RequiresExpenditureReport':
      case 'IsAmendable':
      case 'HasPublicSearch':
      case 'DisplayInMyRecords':
      case 'DisplayInMyWorkQueue':
      case 'DisplayInForms':
      case 'DisplayInFormsManager':
        const tooltip = getCheckboxTooltip(property.k)
        return (
          <Checkbox
            key={property.k}
            title={tooltip}
            checked={property.v || false}
            label={label}
            disabled={
              (editItem && editItem.MatchStringIsRegularExpression) ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false ||
              readOnly
            }
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e })
              setIsDirty(true)
            }}
          />
        )
      case 'IsFirstPage':
        return (
          <Checkbox
            key={property.k}
            checked={property.v || false}
            label={label}
            onChange={(e) => {
              setEditItem({
                ...editItem,
                [property.k]: e,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'ExpiresByDefault':
        return (
          <Checkbox
            key={property.k}
            checked={property.v || false}
            label="Expiration Date Set By System"
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e })
              setIsDirty(true)
            }}
          />
        )
      case 'ReceiptItemGlDistributionConfigs':
        return (
          <ReceiptItemDistribution
            key={property.k}
            property={property}
            onChange={(val) => {
              setEditItem({ ...editItem, [property.k]: val })
              setIsDirty(true)
            }}
            receiptItemTypeId={editItem.ReceiptItemTypeId}
          />
        )
      case 'ImageClassificationDefinitions':
        return (
          <ImageClassificationDefinitions
            key={property.k}
            property={property}
            onChange={(val) => {
              // console.log(val)
              setEditItem({ ...editItem, [property.k]: val })
              setIsDirty(true)
            }}
            ImagePageTypeId={editItem.ImagePageTypeId}
            optionsMap={optionsMap}
          />
        )
      case 'UserSearch':
        if (!isAdding) return null
        return (
          <SettingsUserSearch
            key={property.k}
            requirePayAccount={redirect !== 'businessXmlUserApi'}
            onChange={(user) => {
              setEditItem({
                ...editItem,
                UserId: user.UserId,
                NameReadonly: `${user.FirstName} ${user.LastName}`,
                EmailReadonly: user.Username,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'RegisteredAgentSearch':
        return (
          <SettingsRegisteredAgentSearch
            key={property.k}
            onChange={({ RegisteredAgentRecordId, RegisteredAgentName }) => {
              setEditItem({
                ...editItem,
                RegisteredAgentRecordId,
                RegisteredAgentNameReadonly: RegisteredAgentName,
              })
              setIsDirty(true)
            }}
          />
        )
      case 'RegisteredAgentNameReadonly':
        return editItem.RegisteredAgentNameReadonly ? (
          <div key={property.k} className="property-readonly">
            <label>Registered Agent:&nbsp;</label>
            <div>{editItem.RegisteredAgentNameReadonly}</div>
          </div>
        ) : null
      case 'Users':
        //if (!isAdding) return null
        return (
          <LinkedUserAccounts
            key={property.k}
            updatePayorValue={(accessor, val) => {
              setEditItem({ ...editItem, [property.k]: val })
              setIsDirty(true)
            }}
            linkedAccounts={editItem.Users}
          />
        )
      case 'SealByteArray':
      case 'SignatureByteArray':
        return (
          <SignatureByteArray
            key={property.k}
            updateBytes={(val, file) => {
              setEditItem({ ...editItem, [property.k]: val, ImageFile: file })
              setIsDirty(true)
            }}
            bytes={editItem[property.k] || ''}
          />
        )
      case 'Password':
        if (!isAdding) return null
        return (
          <TextInput
            key={property.k}
            label={'Initial Password'}
            value={property.v || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
      case 'OffsetDays':
        return (
          <TextInput
            key={property.k}
            label={label}
            value={property.v || ''}
            onChange={(e) => {
              const val = e.target.value
              setEditItem({
                ...editItem,
                [property.k]: val.includes('-') ? val.replace('-', '') : val,
              })
              setIsDirty(true)
            }}
            type="number"
            min="0"
            step="1"
            pattern="\d+"
          />
        )
      case 'InitialLapseLength':
      case 'ContinuationLapseLength':
      case 'Priority':
        return (
          <TextInput
            key={property.k}
            label={label}
            value={property.v || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
            type="number"
          />
        )
      case 'Fee':
        return (
          <TextInput
            key={property.k}
            label={label}
            value={property.v || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
            disabled={
              editItem.HasFeeOverride ||
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false
            }
          />
        )
      case 'BarcodesCode':
        return (
          <TextInput
            key={property.k}
            label={'Barcodes'}
            value={property.v || ''}
            disabled={
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false
            }
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
      case 'SortOrder':
        const sortTooltip = getInputTooltip(property.k)
        return (
          <TextInput
            customCss={`width: 20%; min-width: 150px;`}
            key={property.k}
            label={label}
            title={sortTooltip}
            value={property.v || ''}
            disabled={
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false ||
              readOnly
            }
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
            type="number"
            min="0"
            step="1"
            pattern="\d+"
          />
        )
      case 'MatchString':
      case 'ReplacementString':
        return (
          <TextInput
            key={property.k}
            label={label}
            value={property.v || ''}
            disabled={editItem && editItem.MatchStringIsRegularExpression}
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
      default:
        return (
          <TextInput
            key={property.k}
            label={label}
            value={property.v || ''}
            disabled={
              (pageProperties.find((prop) => prop.Name == property.k) &&
                pageProperties.find((prop) => prop.Name == property.k).FieldDisabled) ||
              editItem.WriteableByWebAdmin === false ||
              readOnly
            }
            onChange={(e) => {
              setEditItem({ ...editItem, [property.k]: e.target.value })
              setIsDirty(true)
            }}
          />
        )
    }
  }

  const handleSuccessSave = (data) => {
    handleSaveSuccess(data)
    setIsDirty(false)
  }

  const handleFailedSave = (err) => {
    if (err.DisplayToUser === true && (err.Message || '') !== '') {
      dispatch(setErrorToast(err.Message))
    } else {
      dispatch(setErrorToast('Failed to save setting.'))
    }
    console.error(err)
  }

  const handleSave = () => {
    if (!allowAdd && isAdding) {
      dispatch(setErrorToast('Adding this setting is not permitted.'))
      return
    }
    switch (redirect) {
      case 'applicationSettings':
        api.SAVE_APPLICATION_SETTING(
          isAdding,
          {
            ...editItem,
            AppSettingId: editItem.AppSettingId || 0,
            IsConfigurable: isAdding ? true : editItem.IsConfigurable,
            ReadableByPublic: isAdding ? true : editItem.ReadableByPublic,
            WriteableByWebAdmin: isAdding ? true : editItem.WriteableByWebAdmin,
          },
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'counties':
        api.SAVE_SETTINGS_COUNTIES(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'countries':
        api.SAVE_SETTINGS_COUNTRIES(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'notaryBondCompanies':
        api.SAVE_SETTINGS_NOTARY_BOND_COMPANY(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'complaintReasonTypes':
        api.SAVE_NOTARY_COMPLAINT_STATUS_TYPES(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'complaintStatusTypes':
        api.SAVE_SETTINGS_NOTARY_COMPLAINT_STATUS(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'notaryFilingTypes':
        api.SAVE_SETTINGS_NOTARY_FILING_TYPE(
          { ...editItem, Id: Number(editItem['NotaryFilingTypeId']) },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'notaryRecordTypes':
        api.SAVE_SETTINGS_NOTARY_RECORD_TYPE(
          { ...editItem, Id: Number(editItem['NotaryRecordTypeId']) },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'uccRecordTypes':
        api.SAVE_SETTINGS_UCC_RECORD_TYPE(
          { ...editItem, Id: Number(editItem['UCCRecordTypeId']) },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'uccFilingTypes':
        api.SAVE_SETTINGS_UCC_FILING_TYPE(
          {
            ...editItem,
            Id: Number(editItem['UCCFilingTypeId']),
            ImagePageTypeId: Number(editItem.ImagePageTypeId) || null,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'uccTypes':
        api.SAVE_SETTINGS_UCC_TYPE(
          {
            ...editItem,
            Id: Number(editItem['LienTypeId']) || null,
            UCCLapseTypeId: Number(editItem.UCCLapseTypeId),
            InitialLapseLength: Number(editItem.InitialLapseLength),
            ContinuationLapseLength: Number(editItem.ContinuationLapseLength),
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'uccStatusTypes':
        api.SAVE_SETTINGS_UCC_STATUS_TYPE(
          { ...editItem, Id: Number(editItem['UCCStatusTypeId'] || 0) },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'userXmlApi':
        api.SAVE_SETTINGS_USER_XML_API_ACCESS(editItem.UserId, handleSuccessSave, handleFailedSave)
        break
      case 'businessXmlUserApi':
        if (isAdding && !editItem.UserId) {
          dispatch(setErrorToast('A web user must be selected before saving.'))
          return
        }
        if (!editItem.RegisteredAgentRecordId) {
          dispatch(setErrorToast('A registered agent must be selected before saving.'))
          return
        }
        if (isAdding) {
          api.SAVE_SETTINGS_BUSINESS_XML_USER_ACCESS(
            { UserId: editItem.UserId, RegisteredAgentRecordId: editItem.RegisteredAgentRecordId },
            handleSuccessSave,
            handleFailedSave
          )
        } else {
          api.UPDATE_SETTINGS_BUSINESS_XML_USER_ACCESS(
            editItem.UserId,
            editItem.RegisteredAgentRecordId,
            handleSuccessSave,
            handleFailedSave
          )
        }
        break
      case 'customCorrespondence':
        api.SAVE_SETTINGS_CUSTOM_CORRESPONDENCE_REASON(
          {
            ...editItem,
            SourceTypeId: optionalData.sourceType ? optionalData.sourceType : 0,
            RecordTypeId: optionalData.recordType ? optionalData.recordType : 0,
            MiscellaneousModuleId: optionalData.miscellaneousModule ? optionalData.miscellaneousModule : null,
            FilingTypeId: optionalData.filingType ? optionalData.filingType : 0,
            RecordSubTypeId:
              optionalData.recordSubType !== 'null'
                ? optionalData.recordSubType
                  ? optionalData.recordSubType
                  : null
                : null,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'notaryStatusTypes':
        api.SAVE_SETTINGS_NOTARY_STATUS_TYPE(
          { ...editItem, Id: Number(editItem['NotaryStatusTypeId']) },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'postalCodes':
        api.SAVE_SETTINGS_POSTAL_CODES(
          { ...editItem, CountyId: Number(editItem['County'].Id) },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'states':
        api.SAVE_SETTINGS_STATE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'collateralKeywords':
        api.SAVE_SETTINGS_REGULATED_WORD(
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,
            SourceTypeId: 2000,
            ValidationMessage: '',
            Contains: true,
            RegulatedWordId: isAdding ? 0 : editItem.RegulatedWordId,
            RecordTypeId: 0,
            RecordSubTypeId: 0,
          },
          (res) => handleSuccessSave({ ...res, Id: res.RegulatedWordId }),
          handleFailedSave
        )
        break
      case 'regulatedWords':
        api.SAVE_SETTINGS_REGULATED_WORD(
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,
            SourceTypeId: 169, // Business Filing Source Type Id
            RegulatedWordId: isAdding ? 0 : editItem.RegulatedWordId,
            // RecordTypeId: 0,
            // RecordSubTypeId: 0,
          },
          (res) => handleSuccessSave({ ...res, Id: res.RegulatedWordId }),
          handleFailedSave
        )
        break
      case 'workViews':
        api.SAVE_SETTINGS_WORK_VIEW(
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,
          },
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'wordManagement':
        api.SAVE_SETTINGS_WORD_REPLACEMENT(
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,
            WordReplacementCategoryId: Number(editItem.WordReplacementCategoryId),
          },
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'businessFilingTypes':
        api.SAVE_SETTINGS_BUSINESS_FILING_TYPE(
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,
            BusinessFilingTypeId: isAdding ? 0 : editItem.BusinessFilingTypeId,
            ImagePageTypeId: Number(editItem.ImagePageTypeId),
          },
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'businessRecordTypes':
        api.SAVE_SETTINGS_BUSINESS_RECORD_TYPE(
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,
            BusinessRecordTypeId: isAdding ? 0 : editItem.BusinessRecordTypeId,
            AnnualReportMonthDue: editItem.AnnualReportMonthDue
              ? Number(editItem.AnnualReportMonthDue)
              : 0,
            AnnualReportDayDue: editItem.AnnualReportDayDue
              ? Number(editItem.AnnualReportDayDue)
              : 0,
            AnnualReportFrequencyTypeId: editItem.AnnualReportFrequencyTypeId
              ? Number(editItem.AnnualReportFrequencyTypeId)
              : 1, // because it is a required field.
          },
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'businessRecordSubTypes':
        api.SAVE_SETTINGS_BUSINESS_RECORD_SUB_TYPE(
          isAdding ? 'POST' : 'PUT',
          editItem,
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'businessStatusTypes':
        api.SAVE_SETTINGS_BUSINESS_STATUS_TYPE(
          isAdding ? 'POST' : 'PUT',
          editItem,
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'businessPartyTitle':
        api.SAVE_SETTINGS_BUSINESS_PARTY_TITLE(
          isAdding ? 'POST' : 'PUT',
          editItem,
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'businessPartyType':
        api.SAVE_SETTINGS_BUSINESS_PARTY_TYPE(
          isAdding ? 'POST' : 'PUT',
          editItem,
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'userDetails':
        let userName = !!editItem.Username ? editItem.Username : editItem.Email
        let requestUser = {
          UserId: editItem.UserId,
          Username: userName,
          FirstName: editItem.FirstName,
          MiddleName: editItem.MiddleName,
          LastName: editItem.LastName,
          Email: editItem.Email,
          Roles: editItem.Roles,
          Password: editItem.Password,
          IsLocked: editItem.IsLocked,
          IsActive: editItem.IsActive,
        }

        if(editItem.CountyAccess){
          requestUser['CountyId'] = editItem.CountyAccess.Id;
        }

        if (editItem.Phone) {
          requestUser['Phone'] = editItem.Phone
        }
        api.SAVE_SETTINGS_APP_USERS(
          requestUser,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'userRolesAndRights':
        api.SAVE_SETTINGS_ACCESS_CONTROL_ROLE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'notarySpecialServiceProviders':
        api.UPDATE_SOFTWARE_PROVIDER(editItem, handleSuccessSave, handleFailedSave)
        break
      case 'receiptItems':
        const sum = editItem.ReceiptItemGlDistributionConfigs.reduce(
          (sum, account) => sum + (Number(account.Percentage) || 0),
          0
        )
        if (editItem.ReceiptItemGlDistributionConfigs.length > 0 && sum !== 100) {
          dispatch(setErrorToast('GL Account Total Distribution must be 100%'))
          break
        }
        api.SAVE_SETTINGS_RECEIPT_ITEM_TYPE(
          {
            ...editItem,
            ReceiptItemTypeId: editItem.ReceiptItemTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'generalLedgerAccounts':
        api.SAVE_SETTINGS_GENERAL_LEDGER_ACCOUNT(
          {
            ...editItem,
            GeneralLedgerAccountId: editItem.GeneralLedgerAccountId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'imagePageType':
        api.SAVE_SETTINGS_IMAGE_PAGE_TYPE(
          {
            ...editItem,
            ImagePageTypeId: editItem.ImagePageTypeId || 0,
            AdditionalPageImagePageTypeId:
              Number(editItem.AdditionalPageImagePageTypeId) > 0
                ? Number(editItem.AdditionalPageImagePageTypeId)
                : null,
          },
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'processedThroughDates':
        let saveDate = editItem.Date ? new Date(editItem.Date) : null
        if (editItem.ProcessedThroughDateTypeId === 2) {
          //automatic job
          if (!itemId) saveDate = null
        } else if (editItem.ProcessedThroughDateTypeId === 3) {
          //current date
          saveDate = null
        }
        api.SAVE_PROCESSED_THROUGH_DATE(
          {
            ...editItem,
            Date: saveDate,
            OffsetDays: Number(editItem.OffsetDays),
            ProcessedThroughDateTypeId: Number(editItem.ProcessedThroughDateTypeId),
            SourceTypeId: Number(editItem.SourceTypeId),
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'orderFilingTypes':
        api.SAVE_SETTINGS_ORDER_FILING_TYPE(
          {
            ...editItem,
            OrderFilingTypeId: editItem.OrderFilingTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'trademarkApplicantTypes':
        api.SAVE_SETTINGS_TRADEMARK_APPLICANT_TYPE(
          {
            ...editItem,
            TrademarkApplicantTypeId: editItem.TrademarkApplicantTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'trademarkClassTypes':
        api.SAVE_SETTINGS_TRADEMARK_CLASS_TYPE(
          {
            ...editItem,
            TrademarkClassTypeId: editItem.TrademarkClassTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'trademarkFilingTypes':
        api.SAVE_SETTINGS_TRADEMARK_FILING_TYPE(
          {
            ...editItem,
            TrademarkFilingTypeId: editItem.TrademarkFilingTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'trademarkRecordTypes':
        api.SAVE_SETTINGS_TRADEMARK_RECORD_TYPE(
          {
            ...editItem,
            TrademarkRecordTypeId: editItem.TrademarkRecordTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'trademarkStatusTypes':
        api.SAVE_SETTINGS_TRADEMARK_STATUS_TYPE(
          {
            ...editItem,
            TrademarkStatusTypeId: editItem.TrademarkStatusTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'trademarkApplicantOrganizationTypes':
        api.SAVE_SETTINGS_TRADEMARK_APPLICANT_ORG_TYPE(
          {
            ...editItem,
            TrademarkStatusTypeId: editItem.TrademarkStatusTypeId || 0,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'signerCapacityTypes':
        api.SAVE_SETTINGS_SIGNER_CAPACITY_TYPE(
          {
            ...editItem,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'registeredAgentRecordTypes':
        api.UPDATE_RA_RECORD_TYPE(editItem, handleSuccessSave, handleFailedSave)
        break
      case 'registeredAgentFilingTypes':
        api.UPDATE_RA_FILING_TYPE(
          { ...editItem, RegisteredAgentFilingTypeId: editItem.RegisteredAgentFilingTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'registeredAgentStatusTypes':
        api.UPDATE_RA_STATUS_TYPE(
          { ...editItem, RegisteredAgentStatusTypeId: editItem.RegisteredAgentStatusTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'athleteAgentRecordTypes':
        api.UPDATE_ATHLETE_AGENT_RECORD_TYPE(
          { ...editItem, AthleteAgentRecordTypeId: editItem.AthleteAgentRecordTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'athleteAgentFilingTypes':
        api.UPDATE_ATHLETE_AGENT_FILING_TYPE(
          { ...editItem, AthleteAgentFilingTypeId: editItem.AthleteAgentFilingTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'athleteAgentStatusTypes':
        api.UPDATE_ATHLETE_AGENT_STATUS_TYPE(
          { ...editItem, AthleteAgentStatusTypeId: editItem.AthleteAgentStatusTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'serviceOfProcessStatutes':
        api.UPDATE_SERVICE_OF_PROCESS_STATUTE(
          { ...editItem, StatuteId: editItem.StatuteId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'serviceOfProcessCourts':
        const item = {
          ...editItem,
          CourtId: editItem.CourtId || 0,
          DisplayId: editItem.CourtId || 0, // this makes no sense...
          CourtType: Number(editItem.CourtType),
          County: Number(editItem.County),
        }
        api.UPDATE_SERVICE_OF_PROCESS_COURT(
          item,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'formPermissionSettings':
        api.UPDATE_FORM_PERMISSIONS(
          { ...editItem },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'historicSOS':
        api.UPDATE_HISTORIC_SOS(
          {
            ...editItem,
            SignatureByteArray: null,
            ImageFile: null,
          },
          isAdding ? 'POST' : 'PUT',
          (hsos) => {
            // console.log(hsos)
            if (editItem.ImageFile && (hsos.Id || editItem.Id))
              api.UPDATE_HISTORIC_SOS_SIGNATURE(
                hsos.Id || editItem.Id,
                editItem.ImageFile,
                handleSuccessSave,
                handleFailedSave
              )
            else if (editItem.ImageFile && !hsos.ID && !editItem.Id) {
              console.error('No Historic SOS Id found to upload signature.')
              handleLoadSuccess()
              dispatch(setErrorToast('Failed to upload Signature Image'))
            } else {
              handleSuccessSave()
            }
          },
          handleFailedSave
        )
        break
      case 'recordUserAccess':
        const ConfiguredSourceTypes = {}
        if (editItem.AppliesToIds) {
          editItem.AppliesToIds.forEach((SourceTypeId) => {
            ConfiguredSourceTypes[SourceTypeId] = {
              UseAsPasskeyDefault: editItem.PasskeyDefaultIds.includes(SourceTypeId),
              UseAsRequestAccessDefault: editItem.RequestAccessDefaultIds.includes(SourceTypeId),
              UseAsLoggedInDefault: editItem.LoggedInDefaultIds.includes(SourceTypeId),
              UseAsNotLoggedInDefault: editItem.NotLoggedInDefaultIds.includes(SourceTypeId),
            }
          })
        }
        const ConfiguredMiscellaneousModules = {}
        if (editItem.MiscAppliesToIds) {
          const MiscPasskeyDefaultIds = editItem.MiscPasskeyDefaultIds || []
          const MiscRequestAccessDefaultIds = editItem.MiscRequestAccessDefaultIds || []
          const MiscLoggedInDefaultIds = editItem.MiscLoggedInDefaultIds || []
          const MiscNotLoggedInDefaultIds = editItem.MiscNotLoggedInDefaultIds || []
          editItem.MiscAppliesToIds.forEach((MiscellaneousModuleId) => {
            ConfiguredMiscellaneousModules[MiscellaneousModuleId] = {
              UseAsPasskeyDefault: MiscPasskeyDefaultIds.includes(MiscellaneousModuleId),
              UseAsRequestAccessDefault: MiscRequestAccessDefaultIds.includes(MiscellaneousModuleId),
              UseAsLoggedInDefault: MiscLoggedInDefaultIds.includes(MiscellaneousModuleId),
              UseAsNotLoggedInDefault: MiscNotLoggedInDefaultIds.includes(MiscellaneousModuleId),
            }
          })
        }
        api.UPDATE_RECORD_USER_ACCESS_ROLE(
          {
            ...editItem,
            ConfiguredSourceTypes,
            ConfiguredMiscellaneousModules,
          },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'charityFilingTypes':
        api.UPDATE_CHARITY_FILING_TYPE(
          { ...editItem, CharityFilingTypeId: editItem.CharityFilingTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'charityStatusTypes':
        api.UPDATE_CHARITY_STATUS_TYPE(
          { ...editItem, CharityStatusTypeId: editItem.CharityStatusTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break;
      case 'charityRecordTypes':        
        api.UPDATE_CHARITY_RECORD_TYPE(          
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,
            CharityRecordTypeId: isAdding ? 0 : editItem.CharityRecordTypeId,
            AnnualReportMonthDue: editItem.AnnualReportMonthDue
              ? Number(editItem.AnnualReportMonthDue)
              : 0,
            AnnualReportDayDue: editItem.AnnualReportDayDue
              ? Number(editItem.AnnualReportDayDue)
              : 0,
            AnnualReportFrequencyTypeId: editItem.AnnualReportFrequencyTypeId
              ? Number(editItem.AnnualReportFrequencyTypeId)
              : 1, // because it is a required field.
          },
          handleSuccessSave,
          handleFailedSave
        )
        break
      case 'contractorFilingTypes':
        api.UPDATE_CONTRACTOR_FILING_TYPE(
          { ...editItem, ContractorFilingTypeId: editItem.ContractorFilingTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break 
      case 'contractorStatusTypes':
        api.UPDATE_CONTRACTOR_STATUS_TYPE(
          { ...editItem, ContractorStatusTypeId: editItem.ContractorStatusTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break; 
      case 'contractorRecordTypes':
        api.UPDATE_CONTRACTOR_RECORD_TYPE(
          { ...editItem, ContractorRecordTypeId: editItem.ContractorRecordTypeId || 0 },
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break;
      case 'contractorClassTypes':
        api.UPDATE_CONTRACTOR_CLASS_TYPE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break;
      case 'contractorComplaintTypes':
        api.UPDATE_CONTRACTOR_COMPLAINT_TYPE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break;
      case 'contractorComplaintStatusTypes':
        api.UPDATE_CONTRACTOR_COMPLAINT_STATUS_TYPE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break;
      case 'contractorComplaintReasonTypes':
        api.UPDATE_CONTRACTOR_COMPLAINT_REASON_TYPE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break;
      case 'lobbyistFilingTypes':
        api.UPDATE_LOBBYIST_FILING_TYPE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break 
      case 'lobbyistStatusTypes':
        api.UPDATE_LOBBYIST_STATUS_TYPE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break; 
      case 'lobbyistRecordTypes':        
        api.UPDATE_LOBBYIST_RECORD_TYPE(          
          isAdding ? 'POST' : 'PUT',
          {
            ...editItem,            
            ExpenditureReportMonthDue: editItem.ExpenditureReportMonthDue
              ? Number(editItem.ExpenditureReportMonthDue)
              : 0,
            ExpenditureReportDayDue: editItem.ExpenditureReportDayDue
              ? Number(editItem.ExpenditureReportDayDue)
              : 0,            
          },
          handleSuccessSave,
          handleFailedSave
        )
        break;
        case 'miscellaneousModules':
        api.UPDATE_MISCELLANEOUS_MODULE(
          editItem,
          isAdding ? 'POST' : 'PUT',
          handleSuccessSave,
          handleFailedSave
        )
        break;      
      default:
        //NOT setup yet.
        console.warn(redirect, 'not tied in')
    }
  }

  const countyOptions = useMemo(() => optionsMap.countyOptions, [optionsMap])
  useEffect(() => {
    switch (redirect) {
      case 'signerCapacityTypes':
        api.GET_SOURCE_TYPE_OPTIONS(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                sourceTypeOptions: res.map((t) => ({
                  value: t.Id,
                  label:
                    t.Name && t.Name.toLowerCase().includes('ucc')
                      ? t.Name.replace(/([a-z])([A-Z])/g, '$1 $2')
                          .trim()
                          .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
                          .trim()
                      : t.Name.replace(/([A-Z])/g, ' $1').trim(),
                  moduleName: t.ModuleName,
                })),
              }
            })
          },
          (err) => console.error(err.message)
        )
        break
      case 'receiptItems':
        // this may need to use Promise.All for more options.
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ReceiptItemCategoryType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                receiptItemCategoryTypeOptions: res.map((t) => ({
                  value: t.Id,
                  label: t.Name,
                })),
              }
            })
          },
          (err) => console.error(err)
        )
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'BatchType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                batchTypeOptions: res.map((t) => ({
                  value: t.Id,
                  label: t.Name,
                })),
              }
            })
          },
          (err) => console.error(err.message)
        )
        api.GET_SOURCE_TYPE_OPTIONS(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                sourceTypeOptions: res.map((t) => ({
                  value: t.Id,
                  label:
                    t.Name && t.Name.toLowerCase().includes('ucc')
                      ? t.Name.replace(/([a-z])([A-Z])/g, '$1 $2')
                          .trim()
                          .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
                          .trim()
                      : t.Name.replace(/([A-Z])/g, ' $1').trim(),
                  moduleName: t.ModuleName,
                })),
              }
            })
          },
          (err) => console.error(err.message)
        )
        break
      case 'applicationSettings':
        api.GET_CONFIGURABLE_APP_SETTING_CATEGORIES(
          (res) => {
            setOptionsMap({
              ...optionsMap,
              categoryOptions: res.map((t) => ({
                value: t,
                label: t,
              })),
            })
          },
          (err) => console.error(err.message)
        )
        break
      case 'notarySpecialServiceProviders':
        api.GET_SOFTWARE_TYPES(
          (res) => {
            setOptionsMap({
              ...optionsMap,
              softwareTypeOptions: res.map((item) => {
                return {
                  label: item.Name,
                  id: item.Id,
                }
              }),
            })
          },
          (err) => {
            console.error(err)
          }
        )
        break
      case 'postalCodes':
        api.GET_SETTINGS_COUNTIES(
          (res) => {
            setOptionsMap({
              ...optionsMap,
              countyOptions: res.map((item) => {
                return {
                  label: item.Name,
                  value: item.Id.toString(),
                }
              }),
            })
          },
          (err) => {
            console.error(err)
          }
        )
        break
      case 'contractorComplaintReasonTypes':
        api.GET_CONTRACTOR_COMPLAINT_TYPES_FOR_REASONS_SETTINGS_PAGE(
          (res) => {
            setOptionsMap({
              ...optionsMap,
              contractorComplaintReasonsTypeOptions: res.map((item) => {
                return {
                  label: item.Name,
                  value: item.Id,
                }
              }),
            })
          },
          (err) => {
            console.error(err)
          }
        )
        break
      case 'miscellaneousModules':
        setOptionsMap({
              ...optionsMap,
              miscellaneousSourceTypeConfigurationOptions: SUBROUTES.filter(r => r.IsMiscRecordsType).map((item) => {
                return {
                  label: item.label,
                  value: item.SourceTypeConfigId,
                }
              }),
            })
        break
      case 'complaintReasonTypes':
        api.GET_SETTINGS_NOTARY_COMPLAINT_TYPE(
          (res) => {
            setOptionsMap({
              ...optionsMap,
              notaryComplaintReasonsTypeOptions: res.map((item) => {
                return {
                  label: item.Name,
                  value: item.Id,
                }
              }),
            })
          },
          (err) => {
            console.error(err)
          }
        )
        break
      case 'complaintStatusTypes':
        api.GET_SETTINGS_NOTARY_COMPLAINT_STATUS_TYPES(
          (res) => {
            setOptionsMap({
              ...optionsMap,
              NotaryComplaintTypeId: res.map((item) => {
                return {
                  label: item.Name,
                  value: item.Id.toString(),
                }
              }),
            })
          },
          (err) => {
            console.error(err)
          }
        )
        break
      case 'userDetails':
        api.GET_SETTINGS_ALL_ACTIVE_ACCESS_CONTROL_ROLES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                userRoleOptions: res,
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'businessFilingTypes':
        api.GET_ALL_IMAGE_PAGE_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                imagePageTypeOptions: res.map((type) => {
                  return {
                    value: type.Id,
                    label: type.Name,
                  }
                }),
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'businessRecordTypes':
      case 'charityRecordTypes':
      case 'lobbyistRecordTypes':
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          '',
          'FilingFrequencyType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                filingFrequencyTypeOptions: res.map((t) => ({
                  value: t.Id,
                  label: t.Name,
                })),
              }
            })
          },
          (err) => console.error(err)
        )
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          '',
          'FilingMonthDueType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                FilingMonthDueOptions: res.map((t) => ({
                  value: t.Id,
                  label: t.Name,
                })),
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'businessPartyType':
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          '',
          'BusinessPartyTitle',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                businessPartyTitleOptions: res
                  .sort((a, b) => (a.Name > b.Name ? 1 : -1))
                  .map((t) => ({
                    id: t.Id, // used in CheckboxList
                    value: t.Id,
                    label: t.Name,
                  })),
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'uccFilingTypes':
        api.GET_ALL_IMAGE_PAGE_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                imagePageTypeOptions: res
                  .filter((type) => type.Name.includes('UCC'))
                  .map((type) => {
                    return {
                      value: type.Id,
                      label: type.Name,
                    }
                  }),
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'uccTypes':
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          'UCC',
          'LapseType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                uccLapseTypeOptions: res.map((type) => {
                  return {
                    value: type.Id,
                    label: type.Name,
                  }
                }),
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'collateralKeywords':
        api.GET_REGULATED_WORD_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                collateralKeywordTypeOptions: res
                  .filter((a) => a.Name === 'Regulated')
                  .map((item) => {
                    return {
                      label: item.Name,
                      value: item.Id.toString(),
                    }
                  }),
              }
            })
          },
          (err) => {
            setOptionsMap((om) => {
              return {
                ...om,
                userRoleOptions: [],
              }
            })
            console.error(err)
          }
        )
        api.GET_ALL_IMAGE_PAGE_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                imagePageTypeOptions: res,
              }
            })
          },
          (err) => console.error(err)
        )
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          'ProcessingStatusType',
          '',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                processingStatuses: res,
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'wordManagement':
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          '',
          'WordReplacementCategory',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                wordReplacementCategoryOptions: res.map((t) => ({
                  value: t.Id,
                  label: t.Name,
                })),
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'regulatedWords':
        api.GET_REGULATED_WORD_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                collateralKeywordTypeOptions: res
                  .map((item) => {
                    return {
                      label: item.Name,
                      value: item.Id.toString(),
                    }
                  })
                  .sort((a, b) => (a.label > b.label ? 1 : -1)),
              }
            })
          },
          (err) => {
            console.error(err)
          }
        )
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          'Business',
          'RecordType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                recordTypeOptions: [
                  { value: 0, label: 'Global' },
                  ...res.map((t) => ({
                    value: t.Id,
                    label: t.Name,
                  })),
                ],
              }
            })
          },
          (err) => console.error(err)
        )
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          'Business',
          'RecordSubType',
          (res) => {
            // console.log(res)
            setOptionsMap((om) => {
              return {
                ...om,
                recordSubTypeOptions: [
                  { value: 0, label: 'Global' },
                  ...res.map((t) => ({
                    value: t.Id,
                    label: t.Name,
                  })),
                ],
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'workViews':
        api.GET_SETTINGS_ALL_ACTIVE_ACCESS_CONTROL_ROLES(
          (res) => {
            // console.log(res)
            setOptionsMap((om) => {
              return {
                ...om,
                userRoleOptions: res,
              }
            })
          },
          (err) => {
            setOptionsMap((om) => {
              return {
                ...om,
                userRoleOptions: [],
              }
            })
            console.error(err)
          }
        )
        api.GET_ALL_IMAGE_PAGE_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                imagePageTypeOptions: res,
              }
            })
          },
          (err) => console.error(err)
        )
        api.GET_WORK_VIEW_STATUSES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                processingStatuses: res,
              }
            })
          },console.error)
        break
      case 'imagePageType':
        api.GET_SETTINGS_IMAGE_PAGE_TYPE_CATEGORIES((res) => {
          // console.log(res)
          setOptionsMap((om) => {
            return {
              ...om,
              imagePageTypeCategories: res.map((t) => ({
                value: t.ImagePageCategoryId,
                label: t.Name,
              })),
            }
          })
        }, console.error)
        api.GET_SETTINGS_IMAGE_PAGE_TYPE_ADDITIONAL_PAGE_TYPES((res) => {
          // console.log(res)
          setOptionsMap((om) => {
            return {
              ...om,
              additionalPageTypes: res.map((t) => ({
                value: t.ImagePageTypeId,
                label: t.Description,
              })),
            }
          })
        }, console.error)
        api.GET_SETTINGS_IMAGE_PAGE_TYPE_AZURE_STORAGE_ACCOUNTS((res) => {
          // console.log(res)
          setOptionsMap((om) => {
            return {
              ...om,
              azureStorageAccounts: res.map((t) => ({
                value: t.Id,
                label: t.Name,
              })),
            }
          })
        }, console.error)
        api.GET_SETTINGS_IMAGE_PAGE_TYPE_CLASSIFICATION_CATEGORIES((res) => {
          // console.log(res)
          setOptionsMap((om) => {
            return {
              ...om,
              classificationCategories: res.map((t) => ({
                value: t,
                label: t,
              })),
            }
          })
        }, console.error)
        api.GET_SETTINGS_IMAGE_PAGE_TYPE_CLASSIFICATION_GROUPS((res) => {
          // console.log(res)
          setOptionsMap((om) => {
            return {
              ...om,
              classificationGroups: res.map((t) => ({
                value: t,
                label: t,
              })),
            }
          })
        }, console.error)
        break
      case 'processedThroughDates':
        api.GET_PROCESSED_THROUGH_DATE_TYPES((res) => {
          // console.log(res)
          setOptionsMap((om) => {
            return {
              ...om,
              processedThroughDateTypeOptions: res.map((t) => ({
                value: t.Id,
                label: t.Name,
              })),
            }
          })
        }, console.error)
        api.GET_SOURCE_TYPE_OPTIONS(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                sourceTypeOptions: res.map((t) => ({
                  value: t.Id,
                  label:
                    t.Name && t.Name.toLowerCase().includes('ucc')
                      ? t.Name.replace(/([a-z])([A-Z])/g, '$1 $2')
                          .trim()
                          .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
                          .trim()
                      : t.Name.replace(/([A-Z])/g, ' $1').trim(),
                  moduleName: t.ModuleName,
                })),
              }
            })
          },
          (err) => console.error(err.message)
        )
        api.GET_ALL_IMAGE_PAGE_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                imagePageTypeOptions: res,
              }
            })
          },
          (err) => console.error(err)
        )
        break
      case 'trademarkClassTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'TrademarkRecordType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                trademarkRecordTypeOptions: res.map((t) => ({
                  value: t.Id,
                  label: t.Name,
                })),
              }
            })
          },
          console.error
        )

        break
      case 'userRolesAndRights':
        api.GET_SETTINGS_ALL_ACCESS_CONTROL_RIGHTS((res) => {
          setOptionsMap((om) => {
            return {
              ...om,
              rightOptions: res
                .map((t) => ({
                  id: t.RightId.toString(),
                  label: `${t.Category} - ${t.Name}`,
                }))
                .sort((a, b) => (a.label > b.label ? 1 : -1)),
            }
          })
        }, console.error)
        break
      case 'serviceOfProcessCourts':
        api.GET_SETTINGS_COUNTIES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                countyOptions: res.map((item) => {
                  return {
                    label: item.Name,
                    value: item.Id.toString(),
                  }
                }),
              }
            })
          },
          (err) => {
            console.error(err)
          }
        )
        api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
          '',
          'ServiceOfProcessCourtType',
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                SOPCourtTypeOptions: res.map((item) => {
                  return {
                    label: item.Name,
                    value: item.Id.toString(),
                  }
                }),
              }
            })
          },
          (err) => {
            console.error(err)
          }
        )
        break
      case 'recordUserAccess':
        api.GET_REQUEST_ACCESS_CONFIGURED_SOURCE_TYPES(
          (res) => {
            setOptionsMap((om) => {
              return {
                ...om,
                sourceTypeOptions: res.map((t) => ({
                  ...t,
                  value: t.Id,
                  label:
                    t.Name && t.Name.toLowerCase().includes('ucc')
                      ? t.Name.replace(/([a-z])([A-Z])/g, '$1 $2')
                          .trim()
                          .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
                          .trim()
                      : t.Name.replace(/([A-Z])/g, ' $1').trim(),
                })),
              }
            })
          },
          (err) => console.error(err.message)
        )
        if (SUBROUTES.some((r) => r.IsMiscRecordsType)) {
          api.GET_MISC_MODULES_FOR_RECORD_ACCESS(
            (res) => {
              setOptionsMap((om) => {
                return {
                  ...om,
                  miscModuleOptions: res.map((t) => ({
                    ...t,
                    value: t.Id,
                    label: t.Name,
                  })),
                }
              })
            },
            (err) => console.error(err.message)
          )
        }
        break
      default:
    }
  }, [redirect])

  useEffect(() => {
    if (
      editItem.RecordTypeId !== undefined &&
      editItem.RecordTypeId !== '' &&
      editItem.RecordSubTypeId !== undefined
    ) {
      api.GET_BUSINESS_RECORD_SUB_TYPE_XREFS_BY_RECORD_TYPE(
        editItem.RecordTypeId,
        (xrefRes) => {
          setOptionsMap((om) => {
            return {
              ...om,
              recordTypeSubTypeXref: xrefRes.BusinessRecordSubTypeIds,
            }
          })
        },
        (err) => {
          console.error(err)
          setOptionsMap((om) => {
            return {
              ...om,
              recordTypeSubTypeXref: [],
            }
          })
        }
      )
    }
  }, [editItem])

  return (
    <div className="properties-list">
      {loading ? (
        <Spinner size="medium" themeColor="dark-neutral" />
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <React.Fragment>
          {Object.entries(defaultDTOs[redirect] || {}).map(([k, v]) => {
            return listPropertyComponent({ k: k, v: editItem[k] })
          })}
          {!readOnly && (
            <ContextButton
              rightIcon={
                <div className="checkmark-wrapper right">
                  <RightArrow />
                </div>
              }
              onClick={handleSave}
              disabled={!isDirty}
            >
              {isAdding ? 'Add' : 'Save'}
            </ContextButton>
          )}
          <ChangePassword
            editItem={editItem}
            showChangeStaffPassword={showChangeStaffPassword}
            setShowChangeStaffPassword={setShowChangeStaffPassword}
          />
          <ResetPassword
            email={editItem.Email}
            showResetPasswordConfirm={showResetPasswordConfirm}
            setShowResetPasswordConfirm={setShowResetPasswordConfirm}
          />
        </React.Fragment>
      )}
    </div>
  )
}

export default PropertiesList
